import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog } from "primereact/dialog";
import { Form } from "react-final-form";
import UiTable from "../../../components/table/Table.component";
import UiButton from "../../../components/button/Button";
import UiInputDate from "../../../components/input-date/InputDate.component";
import UiInputText from "../../../components/input-text/InputText.component";
import UiSelect from "../../../components/select/Select.component";
import UiFieldMessage from "../../../components/field-message/FieldMessage.component";
import FormSection from "../../../components/form-section/FormSection.component";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../models/components/field-message.model";
import {
	TableColumn,
	TableColumnTemplate,
} from "../../../models/components/table/table-column.model";
import { TableFilterType } from "../../../models/components/table/table-filter.model";
import { TableMessages } from "../../../models/components/table/table-message.model";
import { TableTemplateButtonsEvent } from "../../../models/components/table/table-template.model";
import { Task } from "../../../models/entities/task.model";
import { ListOption } from "../../../models/misc.model";
import { BackendPagination } from "../../../models/pagination.model";
import { PageProps } from "../../../models/routing.model";
import { TaskRow } from "../../../models/pages/task-row.model";
import { DataTableSortOrderType } from "../../../models/components/table/table-pagination.model";
import { useActiveProvidersInfo } from "../../../hooks/useActiveProvidersInfo";
import { useTasks } from "../../../hooks/useTasks";
import { useTasksCount } from "../../../hooks/useTasksCount";
import { useUpdateTask } from "../../../hooks/useUpdateTask";
import { FilterService } from "../../../services/filter/filter.service";
import { Formatter } from "../../../services/formatter/formatter.service";
import { HelperService } from "../../../services/helpers/helper.service";
import { mappedAxiosError } from "../../../services/http/http.service";
import { StoredCriteriaService } from "../../../services/session/stored-criteria.service";
import { getSessionStorageUser } from "../../../services/session/auth.service";

const ParticipantsTasks = (props: PageProps) => {
	const { t } = useTranslation("common");
	const queryClient = useQueryClient();
	const coaches = useActiveProvidersInfo();
	const updateTaskMutation = useUpdateTask();

	const setColumns = (): TableColumn[] => {
		return [
			{
				field: "participantId",
				title: "PARTICIPANTS.TASKS.TABLE.COLUMNS.participantId",
				sortable: true,
			},
			{
				field: "ref",
				title: "PARTICIPANTS.TASKS.TABLE.COLUMNS.ref",
				sortable: true,
			},
			{
				field: "name",
				title: "PARTICIPANTS.TASKS.TABLE.COLUMNS.name",
				sortable: true,
			},
			{
				field: "category",
				title: "PARTICIPANTS.TASKS.TABLE.COLUMNS.category",
				sortable: true,
			},
			{
				field: "resolvedLabel",
				title: "PARTICIPANTS.TASKS.TABLE.COLUMNS.resolved",
				template: TableColumnTemplate.TRANSLATE,
				sortable: true,
			},
			{
				field: "priority",
				title: "PARTICIPANTS.TASKS.TABLE.COLUMNS.priority",
				sortable: true,
			},
			{
				field: "assignee",
				title: "PARTICIPANTS.TASKS.TABLE.COLUMNS.assignee",
				template: TableColumnTemplate.COACH,
				sortable: true,
			},
			{
				field: "taskDate",
				title: "PARTICIPANTS.TASKS.TABLE.COLUMNS.taskDate",
				template: TableColumnTemplate.DATE,
				sortable: true,
			},
			{
				field: "actionsTemplate",
				template: TableColumnTemplate.BUTTONS,
				sortable: false,
				alignment: "right",
			},
		];
	};

	const [filters, setFilters] = useState(
		StoredCriteriaService.getFilters(StoredCriteriaService.KEYS["participants-tasks"]) ?? {
			filters: [
				{
					field: "category",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [],
					options: {
						props: {
							label: "PARTICIPANTS.TASKS.TABLE.FILTER_TITLE_CATEGORY",
							options: [],
						},
					},
				},
				{
					field: "resolved",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [],
					options: {
						props: {
							label: "PARTICIPANTS.TASKS.TABLE.FILTER_TITLE_RESOLVED",
							options: [
								new ListOption({ id: "true", label: "ENUMS.YES" }),
								new ListOption({ id: "false", label: "ENUMS.NO" }),
							],
						},
					},
				},
				{
					field: "assignee",
					type: TableFilterType.SELECT_MULTIPLE,
					value: [],
					options: {
						props: {
							label: "PARTICIPANTS.TASKS.TABLE.FILTER_TITLE_ASSIGNEE",
							options: [],
							filter: true,
						},
					},
				},
				{
					field: "taskDate",
					type: TableFilterType.DATE_RANGE,
					options: {
						columnClass: "col-12 col-md-3 col-lg-2",
						props: {
							label: "PARTICIPANTS.TASKS.TABLE.FILTER_TITLE_TASK_DATE",
						},
					},
				},
			],
		}
	);

	const [pagination, setPagination] = useState(
		StoredCriteriaService.getPagination(StoredCriteriaService.KEYS["participants-tasks"]) ?? {
			first: 0,
			rows: 10,
			page: 0,
			sortField: "taskDate",
			sortOrder: 1 as DataTableSortOrderType,
			search: undefined,
			filters: filters.filters,
		}
	);

	const backendPagination = useMemo(() => {
		const paginationData = {
			...pagination,
			sortField: HelperService.compoundSortField(setColumns(), pagination),
		};

		let baseFilters = pagination.filters || [];

		if (baseFilters && FilterService.hasSelectedFilters(baseFilters)) {
			baseFilters = baseFilters.map((item) => {
				if (item.field === "taskDate" && item.value) {
					const minDate = item.value[0]
						? Formatter.dateUTCWithoutTime(item.value[0] as Date)
								?.toISOString()
								.split("T")[0]
						: undefined;
					const maxDate = item.value[1]
						? Formatter.dateUTCWithoutTime(item.value[1] as Date)
								?.toISOString()
								.split("T")[0]
						: minDate;

					return { ...item, value: [minDate, maxDate] };
				}
				return item;
			});
		}

		paginationData.filters = baseFilters;

		return new BackendPagination(paginationData);
	}, [pagination]);

	const { data: tasks = [], isLoading, error } = useTasks(backendPagination);
	const { data: count = 0 } = useTasksCount(backendPagination);

	const categoryOptions = useMemo(() => {
		const categories = new Set<string>();
		tasks.forEach((task) => {
			if (typeof task.category === "string" && task.category.trim()) {
				categories.add(task.category);
			}
		});
		return Array.from(categories).map(
			(category) =>
				new ListOption({
					id: category,
					label: category,
				})
		);
	}, [tasks]);

	const assigneeOptions = useMemo(
		() =>
			coaches.data?.map(
				(item) =>
					new ListOption({
						id: item.coachId,
						label: Formatter.fullName(item.firstName, item.lastName),
					})
			) ?? [],
		[coaches.data]
	);

	const mergedFilters = useMemo(() => {
		return {
			...filters,
			filters: filters.filters.map((filter) => {
				if (filter.field === "category") {
					return {
						...filter,
						options: {
							...filter.options,
							props: {
								...filter.options?.props,
								options: categoryOptions,
							},
						},
					};
				}
				if (filter.field === "assignee") {
					return {
						...filter,
						options: {
							...filter.options,
							props: {
								...filter.options?.props,
								options: assigneeOptions,
							},
						},
					};
				}
				return filter;
			}),
		};
	}, [filters, categoryOptions, assigneeOptions]);

	const [taskModal, setTaskModal] = useState<{
		entity: Task | null;
		isVisible: boolean;
		formMessage?: UiFieldMessageProps;
	}>({ entity: null, isVisible: false, formMessage: undefined });

	const editTask = (e: TableTemplateButtonsEvent) => {
		setTaskModal({
			entity: (e.row as TaskRow).entity,
			isVisible: true,
			formMessage: undefined,
		});
	};

	const resolveTask = (e: TableTemplateButtonsEvent) => {
		const entity = (e.row as TaskRow).entity;
		if (!entity.participantId) return;
		updateTaskMutation.mutate({
			id: entity.id,
			body: {
				participantId: entity.participantId,
				resolved: true,
				resolutionSource: "MANUAL",
				resolvedByUserId: getSessionStorageUser().id,
			},
		});
	};

	const closeTaskModal = () => {
		setTaskModal((prev) => ({ ...prev, isVisible: false, formMessage: undefined }));
	};

	const message = useMemo(() => {
		if (isLoading) {
			return {
				severity: FieldMessageSeverity.LOADING,
				label: new TableMessages().loading,
			};
		}

		if (error) {
			return {
				severity: FieldMessageSeverity.DANGER,
				label: new TableMessages().error,
			};
		}

		if (tasks.length === 0) {
			return {
				severity: FieldMessageSeverity.INFO,
				label: new TableMessages().empty,
			};
		}

		return undefined;
	}, [isLoading, error, tasks.length]);

	return (
		<>
			<UiTable
				useAsCard
				dataKey="id"
				title={
					HelperService.isAdminViewMode()
						? "PARTICIPANTS.TASKS.TABLE.TITLE_ADMIN"
						: "PARTICIPANTS.TASKS.TABLE.TITLE_COACH"
				}
				columns={setColumns()}
				value={tasks.map((row) => new TaskRow(row, editTask, resolveTask))}
				message={message}
				isServerPaginated
				pagination={pagination}
				paginationFn={(event) => {
					const newPagination = { ...pagination, ...event };
					setPagination(newPagination);
					StoredCriteriaService.setPagination(
						StoredCriteriaService.KEYS["participants-tasks"],
						newPagination
					);
				}}
				totalRecords={count}
				search={{
					value: pagination.search,
					fn: (value) => {
						const newPagination = {
							...pagination,
							search: value?.trim() || undefined,
							page: 0,
							first: 0,
						};
						setPagination(newPagination);
						StoredCriteriaService.setPagination(
							StoredCriteriaService.KEYS["participants-tasks"],
							newPagination
						);
					},
				}}
				filter={mergedFilters}
				filterFn={(formData) => {
					const newFilters = FilterService.updateFiltersValues(
						mergedFilters.filters,
						formData
					);
					const newPagination = { ...pagination, filters: newFilters, page: 0, first: 0 };
					setFilters({ ...filters, filters: newFilters });
					setPagination(newPagination);
					StoredCriteriaService.set(
						StoredCriteriaService.KEYS["participants-tasks"],
						newPagination,
						{ ...filters, filters: newFilters }
					);
				}}
			/>

			{taskModal.isVisible && taskModal.entity && (
				<Dialog
					header={t("PARTICIPANTS.TASKS.MODAL.TITLE")}
					visible={true}
					breakpoints={{ "960px": "75vw", "640px": "100vw" }}
					style={{ width: "70vw" }}
					modal={true}
					focusOnShow={false}
					draggable={false}
					closable={true}
					onHide={closeTaskModal}
				>
					<Form
						onSubmit={async (data) => {
							const participantId = taskModal.entity?.participantId;
							if (!participantId) {
								setTaskModal((prev) => ({
									...prev,
									formMessage: {
										severity: FieldMessageSeverity.DANGER,
										label: "PARTICIPANTS.TASKS.MODAL.MISSING_PARTICIPANT_ID",
									},
								}));
								return;
							}

							const body: Record<string, unknown> = {
								participantId,
								ref: data.ref,
								name: data.name,
								description: data.description,
								resolved: (data as any).resolved,
								assignee: data.assignee,
								priority: data.priority,
								taskDate: data.taskDate
									? (data.taskDate as Date).toISOString()
									: null,
								severity: data.severity,
								details: data.details,
								reason: data.reason,
								resolutionType: data.resolutionType,
							};

							Object.keys(body).forEach((key) => {
								const value = body[key];
								if (value === undefined || value === "") {
									delete body[key];
								}
							});

							await updateTaskMutation
								.mutateAsync({
									id: taskModal.entity!.id,
									body,
								})
								.then(() => {
									queryClient.invalidateQueries({
										queryKey: ["participants-tasks"],
									});
									queryClient.invalidateQueries({
										queryKey: ["participants-tasks-count"],
									});
									closeTaskModal();
								})
								.catch(() => {
									setTaskModal((prev) => ({
										...prev,
										formMessage: mappedAxiosError(
											undefined,
											"PARTICIPANTS.TASKS.MODAL.MESSAGE_ERROR"
										),
									}));
								});
						}}
						initialValues={{
							ref: taskModal.entity.ref ?? "",
							name: taskModal.entity.name ?? "",
							description: taskModal.entity.description ?? "",
							assignee: taskModal.entity.assignee ?? null,
							priority: taskModal.entity.priority ?? "",
							taskDate: taskModal.entity.taskDate
								? new Date(taskModal.entity.taskDate)
								: null,
							severity: taskModal.entity.severity ?? "",
							details: taskModal.entity.details ?? "",
							reason: taskModal.entity.reason ?? "",
							resolutionType: taskModal.entity.resolutionType ?? "",
						}}
						render={({ handleSubmit, submitting, form }) => (
							<form onSubmit={handleSubmit}>
								<FormSection className="row">
									<div className="col-12 col-sm-6">
										<UiInputText
											id="ref"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.ref"
											name="ref"
											disabled={true}
										/>
									</div>
									<div className="col-12 col-sm-6">
										<UiInputText
											id="name"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.name"
											name="name"
											disabled={submitting}
										/>
									</div>
									<div className="col-12 col-sm-6">
										<UiSelect
											id="assignee"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.assignee"
											name="assignee"
											options={assigneeOptions}
											onChange={(e) =>
												form.change("assignee", e.target.value)
											}
											disabled={submitting}
											filter={true}
										/>
									</div>
									<div className="col-12 col-sm-6">
										<UiSelect
											id="priority"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.priority"
											name="priority"
											options={[
												new ListOption({ id: "LOW", label: "LOW" }),
												new ListOption({ id: "MEDIUM", label: "MEDIUM" }),
												new ListOption({ id: "HIGH", label: "HIGH" }),
												new ListOption({
													id: "CRITICAL",
													label: "CRITICAL",
												}),
											]}
											onChange={(e) =>
												form.change("priority", e.target.value)
											}
											disabled={submitting}
										/>
									</div>
									<div className="col-12 col-sm-6">
										<UiInputDate
											id="taskDate"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.taskDate"
											name="taskDate"
											onChange={(e) =>
												form.change(
													"taskDate",
													e.value as Date | null | undefined
												)
											}
											disabled={true}
										/>
									</div>
									<div className="col-12">
										<UiInputText
											id="description"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.description"
											name="description"
											disabled={submitting}
										/>
									</div>
									<div className="col-12 col-sm-6">
										<UiInputText
											id="severity"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.severity"
											name="severity"
											disabled={submitting}
										/>
									</div>
									<div className="col-12 col-sm-6">
										<UiInputText
											id="reason"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.reason"
											name="reason"
											disabled={submitting}
										/>
									</div>
									<div className="col-12 col-sm-6">
										<UiInputText
											id="resolutionType"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.resolutionType"
											name="resolutionType"
											disabled={true}
										/>
									</div>
									<div className="col-12">
										<UiInputText
											id="details"
											label="PARTICIPANTS.TASKS.MODAL.FIELDS.details"
											name="details"
											disabled={submitting}
										/>
									</div>
								</FormSection>
								{taskModal.formMessage && (
									<div className="form-message">
										<UiFieldMessage {...taskModal.formMessage}></UiFieldMessage>
									</div>
								)}
								<div className="action-buttons">
									<UiButton
										label="UI_COMPONENTS.BUTTONS.CANCEL"
										className="p-button-outlined p-button-rounded"
										type="button"
										onClick={closeTaskModal}
										disabled={submitting}
									/>
									<UiButton
										label="UI_COMPONENTS.BUTTONS.CONFIRM"
										className="p-button-rounded"
										type="submit"
										loading={submitting}
										disabled={submitting}
									/>
								</div>
							</form>
						)}
					/>
				</Dialog>
			)}
		</>
	);
};

export default ParticipantsTasks;
